/**
 * CLI wiring for `cai rule convert`.
 *
 * Round-trips rule content between Claude Code, Cursor, Copilot, and OpenCode
 * without touching the project scaffold — content is read from --in (or stdin),
 * transformed in memory, and written to --out (or stdout).
 *
 * The conversion itself lives in src/sync/convert.ts; this module is just the
 * arg-parse + I/O wrapper.
 */

import { Command } from "commander";
import { readFileSync, writeFileSync } from "node:fs";
import { resolve } from "node:path";
import {
  convertRuleContent,
  defaultPathFor,
  isProvider,
  PROVIDERS,
  UnknownProviderError,
  type Provider,
} from "./sync/convert.js";
import { printError } from "./utils/errors.js";

/**
 * Parse a comma-separated list of provider tokens.  Throws on the first
 * unknown provider so the CLI exits with a clear message.
 */
function parseProviderList(input: string, flagName: string): Provider[] {
  const tokens = input
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
  if (tokens.length === 0) {
    throw new Error(`${flagName} requires at least one provider.`);
  }
  for (const token of tokens) {
    if (!isProvider(token)) {
      throw new UnknownProviderError(token);
    }
  }
  return tokens as Provider[];
}

/**
 * Read all of stdin as utf8 text.  Empty string if stdin is a TTY.
 */
async function readStdin(): Promise<string> {
  return new Promise((resolveStdin) => {
    if (process.stdin.isTTY) return resolveStdin("");
    const chunks: Buffer[] = [];
    process.stdin.on("data", (c) => chunks.push(c as Buffer));
    process.stdin.on("end", () => resolveStdin(Buffer.concat(chunks).toString("utf8")));
    process.stdin.on("error", () => resolveStdin(""));
  });
}

export function registerRuleConvertCommand(program: Command): void {
  const ruleCmd = program
    .command("rule")
    .description("Convert rule content between tool-config providers (no scaffold needed)");

  ruleCmd
    .command("convert")
    .description("Round-trip rule content between providers")
    .requiredOption("--from <provider>", `Source provider (${PROVIDERS.join(", ")})`)
    .requiredOption("--to <providers>", `Target provider(s), comma-separated (${PROVIDERS.join(", ")})`)
    .option("--in <file>", "Read content from this file (default: stdin)")
    .option("--out <file>", "Write content to this file (default: stdout)")
    .action(async (opts: {
      from: string;
      to: string;
      in?: string;
      out?: string;
    }) => {
      try {
        if (!isProvider(opts.from)) {
          throw new UnknownProviderError(opts.from);
        }
        const targets = parseProviderList(opts.to, "--to");

        const inputPath = opts.in ? resolve(process.cwd(), opts.in) : null;
        const source = inputPath !== null
          ? readFileSync(inputPath, "utf8")
          : await readStdin();

        // One output per target.  When there's a single target and --out is
        // given, write to that path.  Otherwise each target gets
        // <name>-converted.<ext> next to the input (or current cwd if stdin).
        const outPath = opts.out ? resolve(process.cwd(), opts.out) : null;
        const baseDir = inputPath !== null
          ? resolve(inputPath, "..")
          : process.cwd();

        const outputs: Array<{ provider: Provider; content: string; path: string }> = [];
        for (const target of targets) {
          const converted = convertRuleContent(source, opts.from, target);
          let targetPath: string;
          if (outPath && targets.length === 1) {
            targetPath = outPath;
          } else if (outPath) {
            // Multiple targets with --out: treat --out as a directory hint
            // by writing <basename>-<provider><ext>.
            const ext = defaultPathFor(target);
            const dot = ext.lastIndexOf(".");
            const base = dot === -1 ? ext : ext.slice(0, dot);
            const suffix = dot === -1 ? "" : ext.slice(dot);
            targetPath = resolve(baseDir, `${base}-${target}${suffix}`);
          } else {
            // No --out: synthesize a default filename for documentation, but
            // still stream to stdout below (the path is informational only).
            targetPath = resolve(baseDir, defaultPathFor(target));
          }
          outputs.push({ provider: target, content: converted, path: targetPath });
        }

        if (outPath && targets.length >= 1) {
          for (const o of outputs) {
            writeFileSync(o.path, o.content, "utf8");
          }
        } else {
          // Stream to stdout.  For a single target, emit the content directly.
          // For multiple targets, emit each block separated by a marker so
          // callers can split them.
          if (outputs.length === 1) {
            process.stdout.write(outputs[0]!.content);
            if (!outputs[0]!.content.endsWith("\n")) process.stdout.write("\n");
          } else {
            for (const o of outputs) {
              process.stdout.write(`<!-- provider: ${o.provider} -->\n`);
              process.stdout.write(o.content);
              if (!o.content.endsWith("\n")) process.stdout.write("\n");
              process.stdout.write(`<!-- /provider: ${o.provider} -->\n\n`);
            }
          }
        }
      } catch (err) {
        printError(err);
        process.exit(1);
      }
    });
}